import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';

import { WizardLayout } from '@ratehub/base-ui';
import {
    AdditionalInformationBanner,
    CIS_PHONE_NUMBER,
    LifeInsuranceTypeMessages,
    ProviderPhoneNumberBlock,
} from '@ratehub/ins-common';

import PAGES from '../definitions/Pages';
import { InsLifeWizardInsuranceTypes } from '../definitions/CurrentDocument';


function withWizardLayout(Page) {
    function WizardLayoutWrapper({
        layoutProps,
        ...pageProps
    }) {
        const {
            breadcrumbPages,
            insuranceType,
            lastEditDate,
        } = layoutProps;

        return (
            <WizardLayout>
                <WizardLayout.TopRowContainer>
                    <AdditionalInformationBanner
                        message={LifeInsuranceTypeMessages[insuranceType]}
                        lastEditDate={lastEditDate}
                    />
                </WizardLayout.TopRowContainer>

                <WizardLayout.ProgressBar
                    value={breadcrumbPages.reduce(
                        (count, page) => page.isCompleted ? count + 1 : count,
                        0,
                    )}
                    max={breadcrumbPages.length}
                />

                <WizardLayout.ProgressIndicator
                    steps={breadcrumbPages.map(page => ({
                        id: page.name,
                        message: MESSAGES[page.name],
                        isActive: page.isActive,
                        isCompleted: page.isCompleted,
                        isDisabled: (!page.isCompleted && !page.hasBeenVisited && !page.isActive),
                    }))}
                    helpMessage={(
                        <FormattedMessage
                            id="InsLifeWizard.Breadcrumbs.estimatedTimeComplete"
                            defaultMessage="Estimated <strong>2 minutes</strong> to complete"
                        />
                    )}
                    topRightModalElement={(
                        <ProviderPhoneNumberBlock
                            phoneNumber={CIS_PHONE_NUMBER}
                            isPadded={false}
                        />
                    )}
                />

                <WizardLayout.Main>
                    <Page {...pageProps} />
                </WizardLayout.Main>
            </WizardLayout>
        );
    }

    WizardLayoutWrapper.propTypes = {
        layoutProps: PropTypes.shape({
            breadcrumbPages: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string,
                    isActive: PropTypes.bool,
                    isCompleted: PropTypes.bool,
                    hasBeenVisited: PropTypes.bool,
                }),
            ),
            lastEditDate: PropTypes.string,
            insuranceType: PropTypes.oneOf(Object.values(InsLifeWizardInsuranceTypes)).isRequired,
        }).isRequired,
    };

    return observer(WizardLayoutWrapper);
}

const MESSAGES = defineMessages({
    [PAGES.COVERAGE_DETAILS.id]: {
        id: 'LifeWizard.Breadcrumbs.CoverageDetailsStep',
        defaultMessage: 'Coverage Details',
    },
    [PAGES.PERSONAL_DETAILS.id]: {
        id: 'LifeWizard.Breadcrumbs.PersonalDetailsStep',
        defaultMessage: 'Personal Details',
    },
});

export default withWizardLayout;
